import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Skeleton, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@1trading/react';
import type { ReactNode } from 'react';
import { QueryErrorBanner } from '@/components/feedback/query-error';
import { type FieldValueRow, FieldValueTable } from '@/components/field-value-table';
import { useFuturesAssessmentExplanation } from '@/hooks/queries/users/use-futures-assessment-explanation';
import type { FuturesAssessmentAttemptExplanation } from '@/services/api/futures-assessment-explanation';
import { formatPoints, formatStoredResult, formatThresholds, getExplanationErrorMessage } from './futures-assessment-explanation';

const buildSummaryRows = ({ explanation, versionInferred }: FuturesAssessmentAttemptExplanation): FieldValueRow[] => [
  { key: 'version', label: 'Version', value: versionInferred ? `${explanation.version} (inferred, not stored)` : explanation.version },
  { key: 'score', label: 'Score', value: formatPoints(explanation.score, explanation.maxScore) },
  { key: 'thresholds', label: 'Thresholds', value: formatThresholds(explanation.thresholds) },
  { key: 'scoreBand', label: 'Score band', value: explanation.scoreBand },
  { key: 'autoFailed', label: 'Auto-failed', value: explanation.autoFailed ? 'Yes' : 'No' },
  { key: 'autoPassed', label: 'Auto-passed', value: explanation.autoPassed ? 'Yes' : 'No' },
  { key: 'manualReviewReasons', label: 'Review reasons', value: explanation.manualReviewReasons.join(', ') || 'None' },
  { key: 'outcome', label: 'Outcome', value: explanation.outcome },
];

const buildSectionRows = ({ explanation }: FuturesAssessmentAttemptExplanation): FieldValueRow[] =>
  explanation.sections.map((section) => ({ key: section.section, label: section.section, value: formatPoints(section.points, section.maxPoints) }));

const ExplanationBody = ({ result }: { result: FuturesAssessmentAttemptExplanation }) => (
  <div className="flex flex-col gap-4">
    {result.matchesStored ? null : (
      <p className="rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-sm">
        The stored result ({formatStoredResult(result.stored)}) differs from re-scoring the answers.{' '}
        {result.versionInferred
          ? "This is a v1 attempt, and v1's scoring changed in May–June 2025, after most v1 attempts were scored."
          : "A reviewer decision or the broker's settings can change an outcome after scoring."}
      </p>
    )}
    <div className="overflow-hidden rounded-md border">
      <FieldValueTable headings={['Summary', '']} rows={buildSummaryRows(result)} />
    </div>
    {result.explanation.sections.length ? (
      <div className="overflow-hidden rounded-md border">
        <FieldValueTable headings={['Section', 'Points']} rows={buildSectionRows(result)} />
      </div>
    ) : null}
    <div className="overflow-hidden rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>#</TableHead>
            <TableHead>Question</TableHead>
            <TableHead>Answer</TableHead>
            <TableHead>Points</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {result.explanation.questions.map((question, index) => (
            <TableRow key={question.question}>
              <TableCell>Q{index + 1}</TableCell>
              <TableCell className="font-mono text-xs">{question.question}</TableCell>
              <TableCell className="break-all font-mono text-xs">{question.answer.join(', ') || '—'}</TableCell>
              <TableCell className="whitespace-nowrap">{formatPoints(question.points, question.maxPoints)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  </div>
);
ExplanationBody.displayName = 'ExplanationBody';

interface FuturesAssessmentExplanationDialogProps {
  pid: string;
  /** The attempt's stored date, exactly as saved */
  date: string;
  title: string;
  onClose: () => void;
}

export const FuturesAssessmentExplanationDialog = ({ pid, date, title, onClose }: FuturesAssessmentExplanationDialogProps) => {
  const query = useFuturesAssessmentExplanation(pid, date);

  const renderContent = (): ReactNode => {
    if (query.isPending) {
      return (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      );
    }
    if (query.isError) {
      return <QueryErrorBanner message={getExplanationErrorMessage(query.error)} onRetry={() => query.refetch()} />;
    }
    return <ExplanationBody result={query.data} />;
  };

  return (
    <Dialog onOpenChange={(open) => (open ? undefined : onClose())} open>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[900px]">
        <DialogHeader>
          <DialogTitle>{title}: scoring</DialogTitle>
        </DialogHeader>
        {renderContent()}
        <DialogFooter>
          <Button onClick={onClose} type="button" variant="outline">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
FuturesAssessmentExplanationDialog.displayName = 'FuturesAssessmentExplanationDialog';
